import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, MapPin } from 'lucide-react';

const DISTRICTS = [
  "All Around Sri Lanka", "Ampara", "Anuradhapura", "Badulla", "Batticaloa", 
  "Colombo", "Galle", "Gampaha", "Hambantota", "Jaffna", "Kalutara", 
  "Kandy", "Kegalle", "Kilinochchi", "Kurunegala", "Mannar", "Matale", 
  "Matara", "Monaragala", "Mullaitivu", "Nuwara Eliya", "Polonnaruwa", 
  "Puttalam", "Ratnapura", "Trincomalee", "Vavuniya"
];

// Group districts by their starting letter
const groupDistrictsByLetter = (districts) => {
  const grouped = {};
  districts.sort((a, b) => a.localeCompare(b)); // Sort districts alphabetically

  districts.forEach((district) => {
    const firstLetter = district[0].toUpperCase();
    if (!grouped[firstLetter]) {
      grouped[firstLetter] = [];
    }
    grouped[firstLetter].push(district);
  });

  return grouped;
};

function DistrictSelect({ filterByDistrict }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (district) => {
    setSelectedDistrict(district);
    filterByDistrict(district === "All Around Sri Lanka" ? "all" : district); // Fix for "All of Sri Lanka"
    setIsOpen(false);
  };

  // Group districts by their starting letter
  const groupedDistricts = groupDistrictsByLetter(DISTRICTS);

  return (
    <div className="relative w-full sm:w-60 md:w-72" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full  bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
                 flex items-center justify-between
                 hover:border-[#6F48E5] transition-all duration-300
                 focus:ring-[#6F48E5] focus:border-[#6F48E5] focus:outline-none p-2.5"
      >
        <div className="flex items-center gap-2">
          <MapPin 
            size={18} 
            className="text-[#6F48E5] hidden sm:block" 
          />
          <span className="truncate font-semibold">
            {selectedDistrict || 'Choose a District'}
          </span>
        </div>
        <ChevronDown 
          size={18} 
          className={`text-gray-500 transition-transform duration-300 
                   ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white rounded-lg shadow-lg 
                      border border-gray-300 overflow-hidden
                      w-full sm:w-[480px] md:w-[640px] lg:w-[800px] left-1/2 transform -translate-x-1/2 scale-100 ">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-4 max-h-96 overflow-y-auto">
            {Object.entries(groupedDistricts).map(([letter, districts]) => (
              <div key={letter} className="space-y-2">
                <div className="font-bold text-2xl text-[#6F48EF]">{letter}</div>
                {districts.map((district, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelect(district)}
                    className={`w-full p-1 rounded-md flex items-start gap-1 text-sm
                             transition-all duration-300 hover:bg-[#6F48E5]/5
                             ${selectedDistrict === district ? 'bg-[#6F48E5]/10' : ''}`}
                  >
                    <div className="text-left min-w-0">
                      <div className="font-medium text-gray-800 truncate">
                        {district}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DistrictSelect;